import React, { useEffect, useState, useRef, useCallback } from "react";
import athenaService from "../../../services/athenaService";
import patientService from "../../../services/patientService";
import { 
  Avatar, 
  Box, 
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Alert,
  TextField,
  Typography,
  useTheme,
  TablePagination,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip
} from "@mui/material";
import { 
  Add as AddIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  ChatBubbleOutline as MessageIcon,
  Description as IntakeFormIcon,
  Event as ScheduleIcon,
  Delete as ArchiveIcon,
  People as PeopleIcon,
  Visibility as ViewIcon,
  Restore as RestoreIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PatientForm from "../../../components/PatientForm";
import "./Patients.css";

// Helper function to format provider names consistently
const formatProviderName = (provider) => {
  const firstName = provider.firstname || '';
  const lastName = provider.lastname || '';
  const degree = provider.providertypeid || '';
  let formattedName = `${firstName} ${lastName}`;
  if (degree) formattedName += `, ${degree}`;
  return formattedName;
};

function Patients() {
  const [loading, setLoading] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);
  const [allPatients, setAllPatients] = useState([]); // Store all patients from API
  const [patients, setPatients] = useState([]); // Filtered patients for display
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeout = useRef(null); // For debouncing
  const [isViewingPatient, setIsViewingPatient] = useState(false);
  const [patientFormOpen, setPatientFormOpen] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPatients, setTotalPatients] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  
  // Appointment data for patient details
  const [pastAppointments, setPastAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [pastAppointmentsPage, setPastAppointmentsPage] = useState(0);
  const [upcomingAppointmentsPage, setUpcomingAppointmentsPage] = useState(0);
  const [appointmentsPerPage, setAppointmentsPerPage] = useState(5);
  
  // Insurance data
  const [patientInsurance, setPatientInsurance] = useState(null);
  const [allAppointmentsData, setAllAppointmentsData] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('1'); // Default to department 1
  const [selectedProvider, setSelectedProvider] = useState('');
  
  // Add state for locations (grouped departments)
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [departmentsByLocation, setDepartmentsByLocation] = useState({});
  
  // Progressive loading state
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const fetchSessionRef = useRef(0);
  
  // Progressive loading constants
  const MAX_RECORDS = 1500;
  const BATCH_SIZE = 200;
  const INITIAL_BATCH = 100;
  
  const navigate = useNavigate();
  const theme = useTheme();

  // Load departments and providers on component mount
  useEffect(() => {
    loadDepartmentsAndProviders();
  }, []);
  
  // Effect to fetch patient data on component mount or when filters change (not search term)
  useEffect(() => {
    getPatients();
  }, [selectedDepartment, selectedProvider, showArchived, selectedLocation]);
  
  // Effect to handle filtering patients client-side when search term changes
  useEffect(() => {
    if (allPatients.length > 0) {
      filterPatients();
    }
  }, [searchTerm, allPatients]); // Add allPatients as dependency
  
  // Function to load departments and providers
  const loadDepartmentsAndProviders = async () => {
    try {
      // Load departments
      const depts = await athenaService.getDepartments();
      
      // Filter out departments with empty providerlist
      const filteredDepts = depts.filter(dept => 
        dept.providerlist && dept.providerlist.length > 0
      );
      
      console.log('Departments with providers:', filteredDepts);
      setDepartments(filteredDepts || []);
      
      // Group departments by location (city)
      const deptsByLocation = {};
      const locationList = [];
      
      filteredDepts.forEach(dept => {
        const locationName = (dept.city || 'Unknown').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
        
        // Initialize location group if it doesn't exist
        if (!deptsByLocation[locationName]) {
          deptsByLocation[locationName] = [];
          locationList.push({
            name: locationName,
            departments: []
          });
        }
        
        // Add department to its location group
        deptsByLocation[locationName].push(dept);
        
        // Update the location list with this department
        const locationIndex = locationList.findIndex(loc => loc.name === locationName);
        if (locationIndex >= 0) {
          locationList[locationIndex].departments.push(dept);
        }
      });
      
      console.log("Departments grouped by location:", deptsByLocation);
      console.log("Location list:", locationList);
      
      setLocations(locationList);
      setDepartmentsByLocation(deptsByLocation);
      
      // Get default department (first department with providers)
      const defaultDept = filteredDepts.length > 0 ? filteredDepts[0] : null;
      
      if (defaultDept) {
        // Set default location
        const defaultLocation = defaultDept.city || 'Unknown';
        setSelectedLocation(defaultLocation);
        
        setSelectedDepartment(defaultDept.departmentid);
        
        // Load providers for the default department
        const provs = await athenaService.getProviders(parseInt(defaultDept.departmentid));
        
        // If department has a providerlist, filter providers to only include those in the list
        if (defaultDept.providerlist && defaultDept.providerlist.length > 0) {
          const filteredProvs = provs.filter(p => 
            defaultDept.providerlist.includes(p.providerid.toString())
          );
          console.log('Initial filtered providers for department:', filteredProvs);
          setProviders(filteredProvs || []);
        } else {
          // If no providerlist is available, use all providers
          setProviders(provs || []);
        }
      }
    } catch (error) {
      console.error("Error loading departments or providers:", error);
      setSnackbar({
        open: true,
        message: 'Failed to load departments or providers: ' + error.message,
        severity: 'error'
      });
    }
  };
  
  // Handle location change
  const handleLocationChange = async (event) => {
    const location = event.target.value;
    console.log("Selected location:", location);
    
    setSelectedLocation(location);
    setSelectedDepartment(''); // Reset department
    setSelectedProvider(''); // Reset provider
    
    // Since department dropdown is hidden, load providers directly when location changes
    if (location && departmentsByLocation[location]) {
      try {
        // Get all departments for this location
        const locationDepts = departmentsByLocation[location];
        if (locationDepts && locationDepts.length > 0) {
          // Use the first department to load providers
          const firstDept = locationDepts[0];
          setSelectedDepartment(firstDept.departmentid.toString());
          
          const provs = await athenaService.getProviders(parseInt(firstDept.departmentid));
          
          // Filter out non-person entities
          const humanProviders = (provs || []).filter(p => p.entitytype === 'Person');
          
          // Format provider names consistently
          const formattedProviders = humanProviders.map(provider => ({
            ...provider,
            formattedDisplayName: formatProviderName(provider)
          }));
          
          setProviders(formattedProviders);
        }
      } catch (error) {
        console.error("Error loading providers for location:", error);
        setProviders([]);
      }
    } else {
      // No location selected, reset providers
      setProviders([]);
    }
  };

  // Handle department change
  const handleDepartmentChange = async (event) => {
    const deptId = event.target.value;
    console.log("Selected department:", deptId);
    setSelectedDepartment(deptId);
    setSelectedProvider(''); // Reset provider
    
    try {
      // Find the department
      const department = departments.find(d => d.departmentid.toString() === deptId.toString());
      
      if (department) {
        // Load providers for the selected department
        const provs = await athenaService.getProviders(parseInt(deptId));
        
        // If department has a providerlist, filter providers to only include those in the list
        if (department.providerlist && department.providerlist.length > 0) {
          const filteredProvs = provs.filter(p => 
            department.providerlist.includes(p.providerid.toString())
          );
          console.log('Filtered providers for department:', filteredProvs);
          setProviders(filteredProvs || []);
        } else {
          // If no providerlist is available, use all providers
          setProviders(provs || []);
        }
      } else {
        console.error('Department not found:', deptId);
        setProviders([]);
      }
    } catch (error) {
      console.error('Error loading providers for department:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load providers: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Handle provider change
  const handleProviderChange = (event) => {
    setSelectedProvider(event.target.value);
  };

  // Helper function to extract patients from appointments
  const extractPatientsFromAppointments = useCallback((appointmentsData, existingPatientMap = new Map()) => {
    const patientMap = new Map(existingPatientMap);
    
    appointmentsData.forEach(apt => {
      if (apt.patient && apt.patient.patientid) {
        const patientId = apt.patient.patientid;
        
        if (apt.patient.status === 'inactive') return;
        
        const appointmentDateTime = apt.scheduleddatetime ? new Date(apt.scheduleddatetime) : null;
        
        if (!patientMap.has(patientId)) {
          const formattedPatient = {
            id: patientId,
            firstName: apt.patient.firstname || '',
            lastName: apt.patient.lastname || '',
            email: apt.patient.email || '',
            phone: apt.patient.mobilephone || apt.patient.homephone || '',
            dob: apt.patient.dob || '',
            sex: apt.patient.sex || '',
            address: apt.patient.address1 || '',
            city: apt.patient.city || '',
            state: apt.patient.state || '',
            zip: apt.patient.zip || '',
            lastAppointment: apt.patient.lastappointment || '',
            nextAppointment: apt.date ? `${apt.date} ${apt.starttime || ''}` : '',
            departmentId: apt.departmentid || apt.patient.departmentid || 1,
            providerId: apt.providerid || apt.patient.primaryproviderid || '',
            patientData: apt.patient,
            status: apt.patient.status || 'active',
            scheduledDateTime: appointmentDateTime,
            rawScheduledDateTime: apt.scheduleddatetime || ''
          };
          patientMap.set(patientId, formattedPatient);
        } else {
          const existingPatient = patientMap.get(patientId);
          if (appointmentDateTime && (!existingPatient.scheduledDateTime || appointmentDateTime > existingPatient.scheduledDateTime)) {
            existingPatient.scheduledDateTime = appointmentDateTime;
            existingPatient.rawScheduledDateTime = apt.scheduleddatetime || '';
            existingPatient.nextAppointment = apt.date ? `${apt.date} ${apt.starttime || ''}` : '';
            patientMap.set(patientId, existingPatient);
          }
        }
      }
    });
    
    return patientMap;
  }, []);

  // Helper function to sort and format patients
  const sortAndFormatPatients = useCallback((patientMap) => {
    return Array.from(patientMap.values())
      .filter(patient => patient.status !== 'inactive')
      .sort((a, b) => {
        if (a.scheduledDateTime && b.scheduledDateTime) {
          return b.scheduledDateTime - a.scheduledDateTime;
        } else if (a.scheduledDateTime) {
          return -1;
        } else if (b.scheduledDateTime) {
          return 1;
        }
        return `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`);
      });
  }, []);

  // Background loading function for remaining appointments
  const loadRemainingPatients = useCallback(async (baseOptions, sessionId, existingPatientMap, existingAppointments) => {
    if (sessionId !== fetchSessionRef.current) {
      console.log('[Patients Progressive] Session expired, aborting');
      return;
    }
    
    setIsLoadingMore(true);
    let patientMap = new Map(existingPatientMap);
    let allAppointments = [...existingAppointments];
    
    // Full date range for remaining (same as Appointments: -30 days to +15 days)
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - 30);
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 15);
    
    const fullOptions = {
      ...baseOptions,
      scheduledstartdate: pastDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      scheduledenddate: futureDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    };
    
    try {
      if (baseOptions.locationDepts && baseOptions.locationDepts.length > 0) {
        // Location mode: load remaining from each department
        for (const dept of baseOptions.locationDepts) {
          if (sessionId !== fetchSessionRef.current) break;
          
          let offset = INITIAL_BATCH;
          const deptOptions = {
            showpatientdetail: true,
            departmentId: parseInt(dept.departmentid),
            ...fullOptions
          };
          if (baseOptions.providerId) deptOptions.providerId = baseOptions.providerId;
          
          while (sessionId === fetchSessionRef.current && offset < MAX_RECORDS) {
            try {
              console.log(`[Patients Progressive] Dept ${dept.departmentid}: offset ${offset}`);
              const batch = await athenaService.getBookedAppointments({
                ...deptOptions,
                limit: BATCH_SIZE,
                offset
              });
              
              if (!batch?.length) break;
              
              allAppointments = [...allAppointments, ...batch];
              patientMap = extractPatientsFromAppointments(batch, patientMap);
              offset += batch.length;
              setLoadedCount(allAppointments.length);
              
              // Throttle UI updates - only every 400 records or final batch
              if (allAppointments.length % 400 < BATCH_SIZE || batch.length < BATCH_SIZE) {
                const sortedPatients = sortAndFormatPatients(patientMap);
                setAllPatients(sortedPatients);
                setPatients(sortedPatients);
                setTotalPatients(sortedPatients.length);
              }
              
              if (batch.length < BATCH_SIZE) break;
            } catch (err) {
              console.error(`[Patients Progressive] Error:`, err);
              break;
            }
          }
        }
      } else {
        // Single department mode
        let offset = INITIAL_BATCH;
        
        while (sessionId === fetchSessionRef.current && offset < MAX_RECORDS) {
          try {
            console.log(`[Patients Progressive] Fetching offset ${offset}`);
            const batch = await athenaService.getBookedAppointments({
              ...fullOptions,
              limit: BATCH_SIZE,
              offset
            });
            
            if (!batch?.length) break;
            
            allAppointments = [...allAppointments, ...batch];
            patientMap = extractPatientsFromAppointments(batch, patientMap);
            offset += batch.length;
            setLoadedCount(allAppointments.length);
            
            // Throttle UI updates - only every 400 records or final batch
            if (allAppointments.length % 400 < BATCH_SIZE || batch.length < BATCH_SIZE) {
              const sortedPatients = sortAndFormatPatients(patientMap);
              setAllPatients(sortedPatients);
              setPatients(sortedPatients);
              setTotalPatients(sortedPatients.length);
            }
            
            if (batch.length < BATCH_SIZE) break;
          } catch (err) {
            console.error(`[Patients Progressive] Error:`, err);
            break;
          }
        }
      }
      
      // Final update - ensure UI reflects all loaded data
      const finalPatients = sortAndFormatPatients(patientMap);
      setAllPatients(finalPatients);
      setPatients(finalPatients);
      setTotalPatients(finalPatients.length);
      setAllAppointmentsData(allAppointments);
    } finally {
      setIsLoadingMore(false);
      console.log('[Patients Progressive] Background loading complete');
    }
  }, [extractPatientsFromAppointments, sortAndFormatPatients, MAX_RECORDS, BATCH_SIZE, INITIAL_BATCH]);

  // Get patients from the API with progressive loading
  const getPatients = async () => {
    setLoading(true);
    setLoadedCount(0);
    
    // Increment session ID
    const currentSession = ++fetchSessionRef.current;
    console.log(`[Patients Progressive] Starting session ${currentSession}`);
    
    let fetchedPatients = [];
    try {
      if (showArchived) {
        // Archived patients - no progressive loading needed
        console.log('Fetching archived patients with filters:', { selectedDepartment, selectedProvider });
        const archivedPatients = await patientService.getArchivedPatients(selectedDepartment, selectedProvider);
        fetchedPatients = archivedPatients;
        setTotalPatients(archivedPatients.length);
        setAllPatients(archivedPatients);
        setPatients(archivedPatients);
      } else {
        // Progressive loading for active patients
        const today = new Date();
        const tenDaysLater = new Date();
        tenDaysLater.setDate(today.getDate() + 10);
        
        const startDate = today.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        const endDate = tenDaysLater.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

        let baseOptions = {
          showpatientdetail: true
        };
        
        if (selectedProvider && selectedProvider !== '') {
          baseOptions.providerId = parseInt(selectedProvider);
        }

        let firstBatchAppointments = [];
        
        // STEP 1: Fast first batch
        if (selectedDepartment && selectedDepartment !== '' && selectedDepartment !== '1') {
          // Specific department
          baseOptions.departmentId = parseInt(selectedDepartment);
          const options = {
            ...baseOptions,
            scheduledstartdate: startDate,
            scheduledenddate: endDate,
            limit: INITIAL_BATCH,
            offset: 0
          };
          console.log("[Patients Progressive] Fast first batch (specific dept):", options);
          firstBatchAppointments = await athenaService.getBookedAppointments(options) || [];
          
        } else if (selectedLocation && departmentsByLocation[selectedLocation]) {
          // Location with all departments - parallel fetch
          const locationDepts = departmentsByLocation[selectedLocation];
          console.log(`[Patients Progressive] Fast first batch from ${locationDepts.length} departments`);
          
          const deptPromises = locationDepts.map(dept => {
            const options = {
              ...baseOptions,
              departmentId: parseInt(dept.departmentid),
              scheduledstartdate: startDate,
              scheduledenddate: endDate,
              limit: INITIAL_BATCH,
              offset: 0
            };
            return athenaService.getBookedAppointments(options).catch(() => []);
          });
          
          const results = await Promise.all(deptPromises);
          firstBatchAppointments = results.flat();
          baseOptions.locationDepts = locationDepts;
          
        } else {
          // Default department 1
          baseOptions.departmentId = 1;
          const options = {
            ...baseOptions,
            scheduledstartdate: startDate,
            scheduledenddate: endDate,
            limit: INITIAL_BATCH,
            offset: 0
          };
          console.log("[Patients Progressive] Fast first batch (default):", options);
          firstBatchAppointments = await athenaService.getBookedAppointments(options) || [];
        }
        
        console.log(`[Patients Progressive] First batch: ${firstBatchAppointments.length} appointments`);
        
        // Extract patients from first batch
        const initialPatientMap = extractPatientsFromAppointments(firstBatchAppointments);
        const initialPatients = sortAndFormatPatients(initialPatientMap);
        
        // Display immediately
        setAllPatients(initialPatients);
        setPatients(initialPatients);
        setTotalPatients(initialPatients.length);
        setAllAppointmentsData(firstBatchAppointments);
        setLoadedCount(firstBatchAppointments.length);
        setLoading(false);
        
        fetchedPatients = initialPatients;
        
        // STEP 2: Background loading (full date range like Appointments)
        console.log("[Patients Progressive] Starting background loading...");
        loadRemainingPatients(baseOptions, currentSession, initialPatientMap, firstBatchAppointments);
      }
    } catch (error) {
      console.error("Error fetching patient data:", error);
      setSnackbar({
        open: true,
        message: 'Failed to load patients: ' + error.message,
        severity: 'error'
      });
      setPatients([]);
      fetchedPatients = [];
    }
    
    setLoading(false);
    return fetchedPatients;
  };

  // Function to filter patients locally based on search term
  const filterPatients = useCallback(() => {
    if (!searchTerm.trim()) {
      // If search term is empty, show all patients
      setPatients(allPatients);
      setTotalPatients(allPatients.length);
      return;
    }
    
    // Filter patients based on the search term
    const term = searchTerm.toLowerCase().trim();
    const filtered = allPatients.filter(patient => {
      const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
      const email = (patient.email || '').toLowerCase();
      const phone = (patient.phone || '').toLowerCase();
      
      return fullName.includes(term) || 
             email.includes(term) || 
             phone.includes(term);
    });
    
    setPatients(filtered);
    setTotalPatients(filtered.length);
  }, [searchTerm, allPatients]);
  
  // Debounced search - only trigger search after user stops typing
  const handleSearchChange = (event) => {
    const value = event.target.value;
    
    // Clear any existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Set search term immediately for input field
    setSearchTerm(value);
    
    // The useEffect will handle the filtering automatically
    // No need to call filterPatients() directly here
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewPatient = (patient) => {
    setSelectedPatient(patient);
    setIsViewingPatient(true);
    
    // Fetch patient appointments and insurance data
    fetchPatientAppointments(patient);
    fetchPatientInsurance(patient);
  };
  
  // Handle pagination for past appointments
  const handlePastAppointmentsPageChange = (event, newPage) => {
    setPastAppointmentsPage(newPage);
  };
  
  // Handle pagination for upcoming appointments
  const handleUpcomingAppointmentsPageChange = (event, newPage) => {
    setUpcomingAppointmentsPage(newPage);
  };
  
  // Handle rows per page change for appointments
  const handleAppointmentsPerPageChange = (event) => {
    setAppointmentsPerPage(parseInt(event.target.value, 10));
    setPastAppointmentsPage(0);
    setUpcomingAppointmentsPage(0);
  };
  


  // Fetch patient appointment history and upcoming appointments
  const fetchPatientAppointments = async (patient) => {
    if (!patient || !patient.id) return;
    
    setLoadingAppointments(true);
    try {
      // Get today's date
      const today = new Date();
      
      // Initialize variable for patient appointments
      let patientAppointments = [];
      
      // First check if we have appointments data already
      if (allAppointmentsData && allAppointmentsData.length > 0) {
        // Filter appointments for this specific patient from the existing allAppointmentsData
        // This reuses the data already fetched for the patient list
        patientAppointments = allAppointmentsData.filter(apt => 
          apt.patientid === patient.id || 
          (apt.patient && apt.patient.patientid === patient.id)
        );
        
        console.log(`Found ${patientAppointments.length} appointments for patient ${patient.id} in existing data`);
      }
      
      // If we don't have appointments in the cache, fetch them directly
      if (!patientAppointments || patientAppointments.length === 0) {
        console.log('No appointments found in cache, fetching directly from API');
        
        const pastDate = new Date();
        pastDate.setDate(pastDate.getDate() - 15);
        const scheduledStartDate = pastDate.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
        
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 30);
        const scheduledEndDate = futureDate.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
        
        try {
          const options = {
            patientid: patient.id,
            scheduledstartdate: scheduledStartDate,
            scheduledenddate: scheduledEndDate,
            showpatientdetail: true
          };
          
          console.log('Fetching appointments directly for patient with options:', options);
          const response = await athenaService.getBookedAppointments(options);
          
          if (response && response.appointments) {
            patientAppointments = response.appointments;
            console.log(`Fetched ${patientAppointments.length} appointments directly for patient ${patient.id}`);
          }
        } catch (aptError) {
          console.error('Error fetching patient appointments directly:', aptError);
        }
        
        if (!patientAppointments || patientAppointments.length === 0) {
          console.warn('No appointments found for this patient after direct fetch');
          setPastAppointments([]);
          setUpcomingAppointments([]);
          return;
        }
      }
      
      // Format and separate past and upcoming appointments
      const formattedAppointments = patientAppointments.map(apt => {
        // Extract appointment type
        const appointmentType = apt.appointmenttype || apt.patientappointmenttypename || '';
        
        // Get provider name - need to lookup if only ID is available
        let providerName = 'Unknown provider';
        if (apt.providername) {
          providerName = apt.providername;
        } else if (apt.provider && (apt.provider.firstname || apt.provider.lastname)) {
          providerName = `${apt.provider.firstname || ''} ${apt.provider.lastname || ''}`.trim();
        } else if (apt.providerid) {
          // Try to find the provider in the providers list
          const providerMatch = providers.find(p => p.providerid.toString() === apt.providerid.toString());
          if (providerMatch) {
            providerName = `${providerMatch.firstname || ''} ${providerMatch.lastname || ''}`.trim();
          } else {
            // If we can't find a match, just use ID
            providerName = `Provider ${apt.providerid}`;
          }
        }
        
        // Map appointment status to readable format
        let status = 'Booked';
        if (apt.appointmentstatus) {
          // Handle athenaNet status codes
          switch(apt.appointmentstatus) {
            case 'x':
              status = apt.cancelreasonname === 'PATIENT NO SHOW' ? "No Show" : "Cancelled";
              break;
            case 'f': 
              status = "Scheduled";
              break;
            case 'o':
              status = "Open";
              break;
            case '2':
              status = "Checked In";
              break;
            case '3':
              status = "Checked Out";
              break;
            case '4':
              status = "Charge Entered";
              break;
            default:
              status = apt.appointmentstatus;
          }
        } else if (apt.status) {
          status = apt.status;
        }
        
        // Format date properly
        let formattedDate = apt.date || apt.appointmentdate || 'Unknown date';
        if (formattedDate) {
          // Remove escape slashes if they exist
          formattedDate = formattedDate.replace(/\\/g, '');
        }
        
        return {
          id: apt.appointmentid,
          date: formattedDate,
          startTime: apt.starttime || '',
          endTime: apt.endtime || '',
          provider: providerName,
          department: apt.departmentname || apt.departmentid || '',
          reason: apt.appointmentreason || apt.reason || appointmentType || 'No reason specified',
          status: status,
          patientId: apt.patientid || patient.id,
          scheduledDateTime: apt.scheduleddatetime ? new Date(apt.scheduleddatetime.replace(/\\/g, '')) : null,
          duration: apt.duration || '',
          copay: apt.appointmentcopay?.insurancecopay || 0,
          insurances: apt.insurances || [],
          rawData: apt // Store raw data for reference
        };
      });
      
      // Sort by date descending (most recent first)
      formattedAppointments.sort((a, b) => {
        if (a.scheduledDateTime && b.scheduledDateTime) {
          return b.scheduledDateTime - a.scheduledDateTime;
        }
        return 0;
      });
      
      // Split into past and upcoming
      const past = [];
      const upcoming = [];
      
      formattedAppointments.forEach(apt => {
        // Parse appointment date
        const aptDate = new Date(apt.date);
        
        if (aptDate < today) {
          past.push(apt);
        } else {
          upcoming.push(apt);
        }
      });
      
      // Update state
      setPastAppointments(past);
      setUpcomingAppointments(upcoming);
      
    } catch (error) {
      console.error('Error fetching patient appointments:', error);
      setPastAppointments([]);
      setUpcomingAppointments([]);
      
      setSnackbar({
        open: true,
        message: 'Failed to load appointment history',
        severity: 'error'
      });
    } finally {
      setLoadingAppointments(false);
    }
  };
  
  // Insurance package ID to name mapping
  const getInsuranceProviderName = (packageId) => {
    // Convert to number for comparison
    const id = Number(packageId);
    
    // Map of insurance package IDs to provider names
    const insuranceMap = {
      98546: "Medicare",
      15310: "Blue Cross",
      74: "Cigna",
      2150: "Blue Shield",
      23344: "Aetna",
      660279: "Health Net",
      4953: "United Healthcare",
      655450: "Tricare",
      581754: "Adventist Health",
      19961: "USC Health Plan",
      190449: "Optum Medical Group"
    };
    
    return insuranceMap[id] || null;
  };
  
  // Get friendly insurance provider name with fallbacks
  const getFormattedInsuranceProvider = (insurance) => {
    if (!insurance) return 'Not Available';
    
    return getInsuranceProviderName(insurance.insurancepackageid) || 
           insurance.insuranceplandisplayname || 
           insurance.ircname || 
           insurance.insurancepayername || 
           insurance.insurancecompanyname || 
           'Not Available';
  };

  // Fetch patient insurance information
  const fetchPatientInsurance = async (patient) => {
    if (!patient || !patient.id) return;
    console.log("Patient for insurance:", patient.id);
    
    setLoadingInsurance(true);
    try {
      // First check if patient already has insurance data directly in the patientData
      // This is rare but possible in some responses
      if (patient.patientData && patient.patientData.insurances && 
          (Array.isArray(patient.patientData.insurances) ? 
           patient.patientData.insurances.length > 0 : patient.patientData.insurances)) {
        console.log('Using existing insurance data from patient record');
        setPatientInsurance(patient.patientData.insurances);
        return;
      }
      
      // Check the existing appointment data for insurance information
      // In our data structure, insurance is in appointments.insurances array
      let patientAppointments = [];
      
      if (allAppointmentsData && allAppointmentsData.length > 0) {
        // Filter appointments for this patient
        patientAppointments = allAppointmentsData.filter(apt => 
          apt.patientid === patient.id ||
          (apt.patient && apt.patient.patientid === patient.id)
        );
        
        console.log(`Checking ${patientAppointments.length} existing appointments for insurance data`);
      } else {
        console.log('No appointment data available for insurance check');
      }
      
      // Look for insurance data directly in the appointments.insurances array
      const appointmentWithInsurance = patientAppointments.find(apt => 
        apt.insurances && Array.isArray(apt.insurances) && apt.insurances.length > 0
      );
      
      if (appointmentWithInsurance) {
        console.log('Found insurance data in appointment.insurances');
        setPatientInsurance(appointmentWithInsurance.insurances);
      } else {
        // Fallback to looking in legacy patient.insurances location
        const aptWithPatientInsurance = patientAppointments.find(apt => 
          apt.patient && apt.patient.insurances && 
          (Array.isArray(apt.patient.insurances) ? apt.patient.insurances.length > 0 : apt.patient.insurances)
        );
        
        if (aptWithPatientInsurance) {
          console.log('Found insurance data in patient.insurances');
          setPatientInsurance(aptWithPatientInsurance.patient.insurances);
        } else {
          console.log('No insurance data found in any appointment');
          setPatientInsurance(null);
        }
      }
    } catch (error) {
      console.error('Error fetching patient insurance:', error);
      setPatientInsurance(null);
    } finally {
      setLoadingInsurance(false);
    }
  };

  const handleBackToList = () => {
    setIsViewingPatient(false);
    setSelectedPatient(null);  };

  const handleAddPatient = () => {
    setEditPatient(null);
    setPatientFormOpen(true);
  };
  
  const handleEditPatient = (patient) => {
    console.log("Editing patient:", patient);
    // Ensure we have a properly formatted patient object for the form
    const formattedPatient = {
      patientid: patient.patientid || patient.id,
      firstname: patient.firstname || patient.firstName,
      lastname: patient.lastname || patient.lastName,
      email: patient.email,
      homephone: patient.homephone,
      mobilephone: patient.mobilephone || patient.phone,
      dob: patient.dob || patient.dateOfBirth,
      address1: patient.address1 || patient.address,
      city: patient.city, 
      state: patient.state,
      zip: patient.zip || patient.zipCode,
      // Safely handle all possible variations of the sex/gender field
      sex: (patient.patientData && patient.patientData.sex) || patient.sex || patient.gender || ''
    };
    
    console.log("Formatted patient:", formattedPatient);
    setEditPatient(formattedPatient);
    setPatientFormOpen(true);
  };
  
  const handleClosePatientForm = () => {
    setPatientFormOpen(false);
    setEditPatient(null);
  };
  
  const handleArchivePatient = async (patient) => {
    if (window.confirm(`Are you sure you want to archive ${patient.firstname || patient.firstName} ${patient.lastname || patient.lastName}?`)) {
      setLoading(true);
      try {
        const patientId = patient.patientid || patient.id;
        const success = await patientService.deletePatient(patientId);
        
        if (success) {
          // Remove patient from the list
          setPatients(prev => prev.filter(p => (p.patientid || p.id) !== patientId));
          
          // If this was the selected patient, clear the selection
          if (selectedPatient && (selectedPatient.patientid === patientId || selectedPatient.id === patientId)) {
            setSelectedPatient(null);
            setIsViewingPatient(false);
          }
          
          setSnackbar({
            open: true,
            message: `Patient ${patient.firstname || patient.firstName} ${patient.lastname || patient.lastName} has been archived`,
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: `Failed to archive patient`,
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Error archiving patient:', error);
        setSnackbar({
          open: true,
          message: `Error: ${error.message || 'Failed to archive patient'}`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleRestorePatient = async (patient) => {
    if (window.confirm(`Are you sure you want to restore ${patient.firstname || patient.firstName} ${patient.lastname || patient.lastName}?`)) {
      setLoading(true);
      try {
        const patientId = patient.patientid || patient.id;
        const result = await patientService.restorePatient(patientId);
        
        if (result) {
          // Remove patient from the archived list
          setPatients(prev => prev.filter(p => (p.patientid || p.id) !== patientId));
          
          setSnackbar({
            open: true,
            message: `Patient ${patient.firstname || patient.firstName} ${patient.lastname || patient.lastName} has been restored`,
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: `Failed to restore patient`,
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Error restoring patient:', error);
        setSnackbar({
          open: true,
          message: `Error: ${error.message || 'Failed to restore patient'}`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSavePatient = async (patientData) => {
    setSavingPatient(true);
    try {
      let result;
      const isEditing = !!editPatient?.patientid;
      const patientId = isEditing ? editPatient.patientid : null;
      
      if (isEditing) {
        // Update existing patient
        result = await athenaService.updatePatient(patientId, patientData);
        setSnackbar({
          open: true,
          message: `Patient ${patientData.firstname} ${patientData.lastname} updated successfully!`,
          severity: 'success'
        });
      } else {
        // Create new patient
        result = await athenaService.createPatient(patientData);
        setSnackbar({
          open: true,
          message: `Patient ${patientData.firstname} ${patientData.lastname} created successfully!`,
          severity: 'success'
        });
      }
      
      // Refresh patient list and get the updated data
      const updatedPatients = await getPatients();
      
      // Close the form
      setPatientFormOpen(false);
      
      // If we were editing a patient that was being viewed, update the selected patient
      // with the refreshed data and maintain the view state
      if (isEditing && isViewingPatient) {
        // Find the updated patient in the refreshed list
        const updatedPatient = updatedPatients.find(p => p.id === patientId.toString());
        
        if (updatedPatient) {
          // Update the selected patient with the fresh data
          setSelectedPatient(updatedPatient);
          // Make sure we're still in viewing mode
          setIsViewingPatient(true);
          
          // Re-fetch related data for the updated patient
          fetchPatientAppointments(updatedPatient);
          fetchPatientInsurance(updatedPatient);
        }
      }
    } catch (error) {
      console.error("Error saving patient:", error);
      setSnackbar({
        open: true,
        message: `Failed to save patient: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSavingPatient(false);
    }
  };
  
  const handleBookAppointment = (patient) => {
    // Store the patient information in sessionStorage with more details for pre-filling
    sessionStorage.setItem('bookingPatientId', patient.id);
    sessionStorage.setItem('bookingPatientFirstName', patient.firstName || '');
    sessionStorage.setItem('bookingPatientLastName', patient.lastName || '');
    
    // Format date of birth if available
    if (patient.dob) {
      let dobDate;
      try {
        // Try to parse the date (might be in various formats)
        dobDate = new Date(patient.dob);
        if (!isNaN(dobDate.getTime())) { // Valid date
          sessionStorage.setItem('bookingPatientDob', dobDate.toISOString());
        }
      } catch (e) {
        console.error('Error parsing patient DOB:', e);
      }
    }
    
    // Set a flag to indicate we want to open the appointment form directly
    sessionStorage.setItem('openAppointmentFormDirectly', 'true');
    
    // Navigate to appointments page where the form will be opened automatically
    navigate('/admin/appointments');
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    return ((firstName?.charAt(0) || '') + (lastName?.charAt(0) || '')).toUpperCase();
  };

  // Use patients directly since filtering is handled by useEffect
  const filteredPatients = patients;

  // Toggle between active and archived patients
  const toggleArchivedView = () => {
    setShowArchived(prev => !prev);
    // Clear selected patient when toggling views
    setSelectedPatient(null);
    setIsViewingPatient(false);
    // Reset pagination
    setPage(0);
  };

  // We no longer return early with a full-page loader
  // Instead, we'll show loading indicators in specific parts of the UI

  // Patient Details Page
  if (isViewingPatient && selectedPatient) {
    return (
      <Box sx={{ 
        width: '100%', 
        height: '100vh',
        bgcolor: theme.palette.background.default,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f5f5f5',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#555',
          borderRadius: '2px',
          '&:hover': {
            backgroundColor: '#333'
          }
        },
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Tooltip title="Back to patients list">
            <IconButton 
              onClick={handleBackToList} 
              sx={{ 
                mr: 2,
                color: theme.palette.text.primary
              }}
              aria-label="back to patients"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            Patient
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <TextField
            placeholder="Search patients"
            size="small"
            sx={{ 
              width: 250, 
              mr: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                bgcolor: theme.palette.background.paper,
                borderColor: theme.palette.divider,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Search patients">
            <IconButton
              sx={{ 
                bgcolor: theme.palette.background.paper, 
                borderRadius: '50%',
                p: 1,
                mr: 1,
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>GO</span>
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ p: 2 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              width: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper
            }}
          >
            <Box sx={{ 
              p: 3, 
              display: 'flex', 
              alignItems: 'flex-start', 
              borderBottom: `1px solid ${theme.palette.divider}` 
            }}>
              <Avatar
                sx={{
                  width: 50,
                  height: 50,
                  bgcolor: theme.palette.secondary.main,
                  color: theme.palette.primary.main,
                  fontSize: '16px',
                  mr: 2
                }}
              >
                {getInitials(selectedPatient.firstName, selectedPatient.lastName)}
              </Avatar>
              <Typography variant="h6" sx={{ flexGrow: 1, color: theme.palette.text.primary }}>
                {selectedPatient.firstName} {selectedPatient.lastName}
              </Typography>
              <Box>
                <Tooltip title="Edit patient">
                  <IconButton 
                    aria-label="edit patient" 
                    sx={{ color: theme.palette.text.primary }}
                    onClick={() => handleEditPatient(selectedPatient)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Book appointment">
                  <IconButton 
                    aria-label="book appointment" 
                    sx={{ color: theme.palette.text.primary }}
                    onClick={() => handleBookAppointment(selectedPatient)}
                  >
                    <ScheduleIcon />
                  </IconButton>
                </Tooltip>
                {/* <Tooltip title="Send message">
                  <IconButton aria-label="send message" sx={{ color: theme.palette.text.primary }}>
                    <MessageIcon />
                  </IconButton>
                </Tooltip> */}
                {/* <Tooltip title="Send intake form">
                  <IconButton aria-label="send intake form" sx={{ color: theme.palette.text.primary }}>
                    <IntakeFormIcon />
                  </IconButton>
                </Tooltip> */}
                <Tooltip title="Archive patient" sx={{ display: 'none' }}>
                  <IconButton aria-label="archive patient" sx={{ color: theme.palette.error.main, display: 'none' }}
                    onClick={() => handleArchivePatient(selectedPatient)}
                  >
                    <ArchiveIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
                <Box sx={{ width: '33.33%', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Mobile Phone Number</Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>{selectedPatient.phone || 'N/A'}</Typography>
                </Box>
                <Box sx={{ width: '33.33%', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Home Phone Number</Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>{selectedPatient.phone || 'N/A'}</Typography>
                </Box>
                <Box sx={{ width: '33.33%', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Gender</Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {selectedPatient.sex === 'M' ? 'Male' : 
                     selectedPatient.sex === 'F' ? 'Female' : 
                     'Unspecified'}
                  </Typography>
                </Box>
                <Box sx={{ width: '33.33%', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Email Address</Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>{selectedPatient.email || 'N/A'}</Typography>
                </Box>
                <Box sx={{ width: '33.33%', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Date of Birth</Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {selectedPatient.dob ? new Date(selectedPatient.dob).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ width: '33.33%', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Patient ID</Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>{selectedPatient.id || 'N/A'}</Typography>
                </Box>
              </Box>
              
              {/* <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 2, 
                  color: theme.palette.text.primary 
                }}
              >
                Patient settings
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: theme.palette.text.secondary,
                        '&.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                      Exclude from Patient Surveys
                    </Typography>
                  }
                />
              </Box> */}
              
              {/* Insurance Details Section */}
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 2, 
                  color: theme.palette.text.primary 
                }}
              >
                Insurance Information
              </Typography>
              
              {loadingInsurance ? (
                <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: theme.palette.primary.main }}></div>
                </Box>
              ) : patientInsurance && (Array.isArray(patientInsurance) ? patientInsurance.length > 0 : patientInsurance) ? (
                <Box sx={{ mb: 4 }}>
                  {Array.isArray(patientInsurance) ? (
                    patientInsurance.map((insurance, index) => (
                      <Paper key={index} elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Insurance Provider</Typography>
                            <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                              {getFormattedInsuranceProvider(insurance)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Phone Number</Typography>
                            <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                              {insurance.insurancephone || 'Not available'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Member ID</Typography>
                            <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                              {insurance.insuranceidnumber || insurance.memberid || insurance.insuranceid || 'Not available'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Group Number</Typography>
                            <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                              {insurance.groupnumber || insurance.insurancepackageid || 'Not available'}
                            </Typography>
                          </Grid>
                          {/* {insurance.eligibilitystatus && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Box 
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: insurance.eligibilitystatus === 'Eligible' ? theme.palette.success.main : theme.palette.warning.main,
                                    mr: 1
                                  }}
                                />
                                <Typography variant="body2">
                                  {insurance.eligibilitystatus} 
                                  {insurance.eligibilitylastchecked && `(Last checked: ${insurance.eligibilitylastchecked.replace(/\\/g, '')})`}
                                </Typography>
                              </Box>
                              {insurance.eligibilitymessage && (
                                <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary }}>
                                  {insurance.eligibilitymessage}
                                </Typography>
                              )}
                            </Grid>
                          )} */}
                        </Grid>
                      </Paper>
                    ))
                  ) : (
                    <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Insurance Provider</Typography>
                          <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                            {getFormattedInsuranceProvider(patientInsurance)}
                          </Typography>
                        </Grid>
              
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Member ID</Typography>
                          <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                            {patientInsurance.insuranceidnumber || patientInsurance.memberid || patientInsurance.insuranceid || 'Not available'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Group Number</Typography>
                          <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                            {patientInsurance.groupnumber || patientInsurance.insurancepackageid || 'Not available'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Phone Number</Typography>
                          <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                            {patientInsurance.insurancephone || 'Not available'}
                          </Typography>
                        </Grid>
                        {/* {patientInsurance.eligibilitystatus && (
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <Box 
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: patientInsurance.eligibilitystatus === 'Eligible' ? theme.palette.success.main : theme.palette.warning.main,
                                  mr: 1
                                }}
                              />
                              <Typography variant="body2">
                                {patientInsurance.eligibilitystatus} 
                                {patientInsurance.eligibilitylastchecked && `(Last checked: ${patientInsurance.eligibilitylastchecked.replace(/\\/g, '')})`}
                              </Typography>
                            </Box>
                            {patientInsurance.eligibilitymessage && (
                              <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary }}>
                                {patientInsurance.eligibilitymessage}
                              </Typography>
                            )}
                          </Grid>
                        )} */}
                      </Grid>
                    </Paper>
                  )}
                </Box>
              ) : (
                <Box sx={{ py: 2, textAlign: 'center', mb: 4 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>No insurance information available</Typography>
                </Box>
              )}
              </Box>
              
              {/* Upcoming Appointments Section */}
              <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 1 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 3, 
                    color: theme.palette.text.primary 
                  }}
                >
                  Upcoming Appointments
                </Typography>
                
                <Box sx={{borderRadius: 1, overflow: 'hidden' }}>
                {loadingAppointments ? (
                  <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: theme.palette.primary.main }}></div>
                  </Box>
                ) : upcomingAppointments.length > 0 ? (
                  <>
                    <Box sx={{ 
                      display: 'flex', 
                      py: 1,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }}>
                      <Typography variant="body2" sx={{ width: '25%', fontWeight: 'bold', color: theme.palette.text.primary }}>DATE</Typography>
                      <Typography variant="body2" sx={{ width: '25%', fontWeight: 'bold', color: theme.palette.text.primary }}>PROVIDER</Typography>
                      <Typography variant="body2" sx={{ width: '25%', fontWeight: 'bold', color: theme.palette.text.primary }}>REASON</Typography>
                      <Typography variant="body2" sx={{ width: '25%', fontWeight: 'bold', color: theme.palette.text.primary }}>STATUS</Typography>
                    </Box>
                    
                    {upcomingAppointments
                      .slice(upcomingAppointmentsPage * appointmentsPerPage, upcomingAppointmentsPage * appointmentsPerPage + appointmentsPerPage)
                      .map((appointment, index) => (
                        <Box 
                          key={appointment.id || index} 
                          sx={{ 
                            display: 'flex', 
                            py: 1.5,
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' },
                            cursor: 'pointer'
                          }}
                          onClick={() => console.log('View appointment details:', appointment)}
                        >
                          <Typography variant="body2" sx={{ width: '25%', color: theme.palette.text.primary }}>
                            {appointment.date} {appointment.startTime}
                          </Typography>
                          <Typography variant="body2" sx={{ width: '25%', color: theme.palette.text.primary }}>
                            {appointment.provider}
                          </Typography>
                          <Typography variant="body2" sx={{ width: '25%', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {appointment.reason}
                          </Typography>
                          <Typography variant="body2" sx={{ width: '25%', color: theme.palette.text.primary }}>
                            {appointment.status}
                          </Typography>
                        </Box>
                    ))}
                    
                    {upcomingAppointments.length > appointmentsPerPage && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <TablePagination
                          component="div"
                          count={upcomingAppointments.length}
                          page={upcomingAppointmentsPage}
                          onPageChange={handleUpcomingAppointmentsPageChange}
                          rowsPerPage={appointmentsPerPage}
                          onRowsPerPageChange={handleAppointmentsPerPageChange}
                          rowsPerPageOptions={[5, 10, 25]}
                          labelRowsPerPage="Rows"
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>No upcoming appointments</Typography>
                  </Box>
                )}
                </Box>
              </Paper>

              {/* Past Appointments Section */}
              <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 1 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 3, 
                    color: theme.palette.text.primary 
                  }}
                >
                  Appointment History
                </Typography>
                
                <Box sx={{ borderRadius: 1, overflow: 'hidden' }}>
                {loadingAppointments ? (
                  <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: theme.palette.primary.main }}></div>
                  </Box>
                ) : pastAppointments.length > 0 ? (
                  <>
                    <Box sx={{ 
                      display: 'flex', 
                      py: 1,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }}>
                      <Typography variant="body2" sx={{ width: '25%', fontWeight: 'bold', color: theme.palette.text.primary }}>DATE</Typography>
                      <Typography variant="body2" sx={{ width: '25%', fontWeight: 'bold', color: theme.palette.text.primary }}>PROVIDER</Typography>
                      <Typography variant="body2" sx={{ width: '25%', fontWeight: 'bold', color: theme.palette.text.primary }}>REASON</Typography>
                      <Typography variant="body2" sx={{ width: '25%', fontWeight: 'bold', color: theme.palette.text.primary }}>STATUS</Typography>
                    </Box>
                    
                    {pastAppointments
                      .slice(pastAppointmentsPage * appointmentsPerPage, pastAppointmentsPage * appointmentsPerPage + appointmentsPerPage)
                      .map((appointment, index) => (
                        <Box 
                          key={appointment.id || index} 
                          sx={{ 
                            display: 'flex', 
                            py: 1.5,
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' },
                            cursor: 'pointer'
                          }}
                          onClick={() => console.log('View appointment details:', appointment)}
                        >
                          <Typography variant="body2" sx={{ width: '25%', color: theme.palette.text.primary }}>
                            {appointment.date} {appointment.startTime}
                          </Typography>
                          <Typography variant="body2" sx={{ width: '25%', color: theme.palette.text.primary }}>
                            {appointment.provider}
                          </Typography>
                          <Typography variant="body2" sx={{ width: '25%', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {appointment.reason}
                          </Typography>
                          <Typography variant="body2" sx={{ width: '25%', color: theme.palette.text.primary }}>
                            {appointment.status}
                          </Typography>
                        </Box>
                    ))}
                    
                    {pastAppointments.length > appointmentsPerPage && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <TablePagination
                          component="div"
                          count={pastAppointments.length}
                          page={pastAppointmentsPage}
                          onPageChange={handlePastAppointmentsPageChange}
                          rowsPerPage={appointmentsPerPage}
                          onRowsPerPageChange={handleAppointmentsPerPageChange}
                          rowsPerPageOptions={[5, 10, 25]}
                          labelRowsPerPage="Rows"
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>No appointment history</Typography>
                  </Box>
                )}
                </Box>
              </Paper>
            </Paper>
        </Box>
        
        {/* Patient Form Dialog */}
        <PatientForm
          open={patientFormOpen}
          onClose={handleClosePatientForm}
          onSave={handleSavePatient}
          initialData={editPatient || {}}
        />
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Patient List Page
  return (
    <Box sx={{ 
      height: '100vh', 
      width: '100%',
      bgcolor: theme.palette.background.default,
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: '#f5f5f5',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#555',
        borderRadius: '2px',
        '&:hover': {
          backgroundColor: '#333'
        }
      },
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Paper 
        elevation={0} 
        sx={{ 
          width: '100%', 
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: theme.palette.background.paper,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.primary.main,
              width: 30,
              height: 30,
              mr: 2
            }}
          >
            <PeopleIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
              Patients
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                color: theme.palette.text.secondary 
              }}
            >
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            placeholder="Search patients"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ 
              mr: 2, 
              width: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                bgcolor: theme.palette.background.paper,
                borderColor: theme.palette.divider,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Search patients">
            <IconButton
              sx={{ 
                bgcolor: theme.palette.background.paper, 
                borderRadius: '50%',
                p: 1,
                mr: 1,
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>GO</span>
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPatient}
            sx={{ 
              borderRadius: 4,
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark
              },
              display: showArchived ? 'none' : 'flex'
            }}
          >
            Add new patient
          </Button>
        </Box>
      </Paper>

      {/* Filter Section */}
      <Paper elevation={0} sx={{ 
        p: 2, 
        bgcolor: 'background.paper', 
        borderRadius: 1,
        borderBottom: 1, 
        borderColor: 'divider',
        mb: 3,
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          maxWidth: '800px',
          width: '100%'
        }}>
          <FormControl fullWidth sx={{ maxWidth: { xs: '100%', md: 200 } }}>
            <InputLabel 
              id="location-select-label"
              shrink={true}
            >
              Location
            </InputLabel>
            <Select
              labelId="location-select-label"
              value={selectedLocation}
              onChange={handleLocationChange}
              label="Location"
              size="small"
              displayEmpty
              renderValue={(value) => value === "" ? "All Locations" : value}
            >
              <MenuItem value="">All Locations</MenuItem>
              {Object.keys(departmentsByLocation).map((locationName) => (
                <MenuItem key={locationName} value={locationName}>
                  {locationName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth sx={{ maxWidth: { xs: '100%', md: 200 },display: 'none' }} >
            <InputLabel 
              id="department-select-label"
              shrink={true}
            >
              Department
            </InputLabel>
            <Select
              labelId="department-select-label"
              value={selectedDepartment}
              onChange={handleDepartmentChange}
              label="Department"
              size="small"
              disabled={!selectedLocation}
              displayEmpty
              renderValue={(value) => value === "" ? "All Departments" : selectedLocation && departmentsByLocation[selectedLocation] ? 
                departmentsByLocation[selectedLocation].find(d => d.departmentid.toString() === value.toString())?.name || "All Departments" :
                departments.find(d => d.departmentid.toString() === value.toString())?.name || "All Departments"}
            >
              <MenuItem value="">All Departments</MenuItem>
              {selectedLocation && departmentsByLocation[selectedLocation] ? 
                departmentsByLocation[selectedLocation].map((dept) => (
                  <MenuItem key={dept.departmentid} value={dept.departmentid}>
                    {dept.name}
                  </MenuItem>
                )) : 
                departments.map((dept) => (
                  <MenuItem key={dept.departmentid} value={dept.departmentid}>
                    {dept.name}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
          
          <FormControl fullWidth sx={{ maxWidth: { xs: '100%', md: 200 } }}>
            <InputLabel 
              id="provider-select-label"
              shrink={true}
            >
              Provider
            </InputLabel>
            <Select
              labelId="provider-select-label"
              value={selectedProvider}
              onChange={handleProviderChange}
              label="Provider"
              size="small"
              disabled={providers.length === 0}
              displayEmpty
              renderValue={(value) => value === "" ? "All Providers" : 
                providers.find(p => p.providerid.toString() === value.toString())
                  ? formatProviderName(providers.find(p => p.providerid.toString() === value.toString()))
                  : "All Providers"}
            >
              <MenuItem value="">All Providers</MenuItem>
              {providers.map((prov) => (
                <MenuItem key={prov.providerid} value={prov.providerid}>
                  {formatProviderName(prov)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="contained" 
            sx={{ 
              minWidth: '120px',
              height: '40px',
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              }
            }}
            onClick={getPatients}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 mr-2" 
                     style={{ borderColor: 'white' }}></div>
                Loading...
              </>
            ) : 'Apply Filters'}
          </Button>
        </Box>
      </Paper>

      <Box sx={{ px: 3, width: '100%', mb: 3 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            width: '100%',
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Patients
            </Typography>
            {/* <FormControlLabel
              control={
                <Checkbox
                  checked={showArchived}
                  onChange={toggleArchivedView}
                  color="primary"
                />
              }
              label="Show Archived Patients"
            /> */}
          </Box>
          {loading ? (
            // Show loading overlay just for the patient list area
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              p: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            }}>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mb-2" style={{ borderColor: theme.palette.primary.main }}></div>
              <Typography variant="body2" color="textSecondary">
                Loading patients...
              </Typography>
            </Box>
          ) : filteredPatients.length > 0 ? (
            filteredPatients
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((patient) => (
              <Paper
                key={patient.id}
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                  borderRadius: 0,
                  mb: 0,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.paper,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    color: theme.palette.primary.main,
                    width: 40,
                    height: 40,
                    mr: 2,
                    fontSize: '14px'
                  }}
                >
                  {getInitials(patient.firstName, patient.lastName)}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    {patient.firstName} {patient.lastName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    ID: {patient.id} • {patient.phone || 'No phone number'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {patient.dob ? `DOB: ${new Date(patient.dob).toLocaleDateString()}` : ''}
                    {patient.email ? ` • ${patient.email}` : ''}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Book appointment">
                    <IconButton
                      sx={{ mr: 1, color: theme.palette.text.primary }}
                      onClick={() => handleBookAppointment(patient)}
                    >
                      <ScheduleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit patient">
                    <IconButton
                      sx={{ mr: 1, color: theme.palette.text.primary }}
                      onClick={() => handleEditPatient(patient)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleViewPatient(patient)}
                    sx={{
                      borderRadius: 4,
                      minWidth: 0,
                      padding: '4px 8px',
                      marginLeft: 1
                    }}
                  >
                    View
                  </Button>
                  {showArchived ? (
                    // Restore button for archived patients
                    <Tooltip title="Restore patient">
                      <IconButton
                        size="small"
                        aria-label="restore patient"
                        sx={{ ml: 1, color: theme.palette.success.main }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row selection
                          handleRestorePatient(patient);
                        }}
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    // Archive button for active patients
                   <Tooltip title="Archive patient" sx={{ display: 'none' }}>
                      <IconButton
                        size="small"
                        aria-label="archive patient"
                        sx={{ ml: 1, color: theme.palette.error.main, display: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row selection
                          handleArchivePatient(patient);
                        }}
                      >
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            ))
          ) : (
            <Typography variant="h6" align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>
              {searchTerm ? 'No patients match your search criteria.' : 'No patients found.'}
            </Typography>
          )}
        </Paper>
      </Box>
      
      {/* Pagination */}
      {filteredPatients.length > 0 && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Progressive loading indicator */}
          {isLoadingMore && (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Loading more patients... ({loadedCount} appointments processed)
            </Typography>
          )}
          {!isLoadingMore && <Box />}
          <TablePagination
            component="div"
            count={filteredPatients.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>
      )}
      
      {/* Patient Form Dialog */}
      <PatientForm
        open={patientFormOpen}
        onClose={handleClosePatientForm}
        onSave={handleSavePatient}
        initialData={editPatient || {}}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Patients;
